import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useActor } from './useActor';
import { useWallet } from '../contexts/WalletContext';
import { type CustomTokenPair, type HTLC, type SwapOffer, type FeeConfig, type AnalyticsData, type VisitorStats, type ValidationCategory, type ValidationReport, type ValidationResult, type AdminActivity, type RavencoinAsset, type RavencoinNFT, type OnChainAuditEntry, type RavencoinOrder, type RavencoinOrderType, type RavencoinHTLC, type OrderDepth, type ApiKey, type RavencoinConfirmationsConfig } from '../backend';
import { Principal } from '@dfinity/principal';
import { toast } from 'sonner';
import { fetchWalletAssetBalances, fetchAssetMetadata, ipfsMetadataToGateway, extractIpfsHash, fetchRavencoinNFTsDirectRPC, fetchIpfsMetadata } from '../lib/ravencoinRpcClient';

// Note: useActor is exported from useActor.ts directly - do not re-export here to avoid circular dependencies

const NFT_NAME_HINTS = ['nft', 'nftrvn', 'nifty', 'ravennft'];

function nameSuggestsNFT(name: string) {
    const lower = name.toLowerCase();
    return NFT_NAME_HINTS.some((hint) => lower.includes(hint));
}

function unpackMetadata(
    metadata?: [] | [string] | string | null
): string | undefined {
    if (!metadata) return undefined;
    if (Array.isArray(metadata)) {
        return metadata[0] ?? undefined;
    }
    if (typeof metadata === 'string') {
        return metadata;
    }
    return undefined;
}

export async function classifyWalletBalances(
    walletAddress: string,
    cachedMap: Map<string, RavencoinAsset>,
    queryClient?: QueryClient
): Promise<{
    assets: RavencoinAsset[];
    nfts: RavencoinNFT[];
}> {
    console.log(`[NFT Classify] Starting for ${walletAddress}, cached assets: ${cachedMap.size}`);
    let balances: Record<string, number> = {};
    try {
        console.log(`[NFT Classify] Calling fetchWalletAssetBalances for ${walletAddress}`);
        balances = await fetchWalletAssetBalances(walletAddress);
        console.log(`[NFT Classify] Got ${Object.keys(balances).length} asset balances:`, Object.keys(balances).slice(0, 10));
    } catch (error) {
        console.error(`[NFT Classify] Failed to fetch balances:`, error);
        // Re-throw to let caller handle fallback
        throw error;
    }
    const entries = Object.entries(balances);
    const assets: RavencoinAsset[] = [];
    const nftResults: RavencoinNFT[] = [];

    // First pass: classify and display NFTs immediately using cached data
    const needsMetadataFetch: Array<{ assetName: string; quantity: number; nameHint: boolean; quantityHint: boolean }> = [];
    
    for (const [assetName, quantity] of entries) {
        const cached = cachedMap.get(assetName);
        const cachedMetadataText = unpackMetadata(cached?.metadata);
        let metadataText = cachedMetadataText;
        // Only mark as NFT if cached data explicitly says so (which means it has IPFS metadata)
        // Don't auto-mark based on quantity/name alone - require actual NFT characteristics
        let isNFT = cached?.isNFT ?? false;
        const lowerName = assetName.toLowerCase();
        const nameHint = nameSuggestsNFT(lowerName);
        const quantityHint = quantity <= 1;

        // Track which assets might be NFTs and need metadata fetch
        // But don't mark as NFT until we confirm they have IPFS metadata
        if (!metadataText && (nameHint || quantityHint || isNFT)) {
            needsMetadataFetch.push({ assetName, quantity, nameHint, quantityHint });
        }

        // Only include as NFT if:
        // 1. Cached data says it's an NFT (which means it has IPFS metadata), OR
        // 2. We have metadata text with IPFS hash (actual NFT characteristic), OR
        // 3. Quantity <= 1 and name/quantity hints suggest NFT (will verify with RPC in background)
        const hasIpfsMetadata = metadataText && extractIpfsHash(metadataText);
        const mightBeNFT = (nameHint || quantityHint) && quantity <= 1;
        if (isNFT || hasIpfsMetadata || mightBeNFT) {
            // Generate image URL immediately from cached metadata or IPFS hash
            let imageUrl: string | undefined;
            if (metadataText) {
                const ipfsHash = extractIpfsHash(metadataText);
                if (ipfsHash) {
                    // Start with direct gateway URL - will be enhanced in background if it's JSON
                    imageUrl = `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
                    
                    // Try to fetch IPFS JSON metadata in background (non-blocking)
                    if (queryClient) {
                        fetchIpfsMetadata(ipfsHash).then((ipfsData: Record<string, any> | null) => {
                            if (ipfsData?.image || ipfsData?.image_url) {
                                const betterImageUrl = ipfsData.image || ipfsData.image_url;
                                // Find the NFT in the current results array
                                const currentCache = queryClient.getQueryData<RavencoinNFT[]>([
                                    'ravencoinWalletNFTCache',
                                    walletAddress,
                                ]) || nftResults;
                                const nftIndex = currentCache.findIndex((n) => n.assetName === assetName);
                                if (nftIndex >= 0) {
                                    const updated = [...currentCache];
                                    updated[nftIndex] = { ...updated[nftIndex], imageUrl: [betterImageUrl] };
                                    queryClient.setQueryData(['ravencoinWalletNFTCache', walletAddress], updated);
                                    queryClient.invalidateQueries({ queryKey: ['ravencoinNFTs', walletAddress] });
                                    console.log(`[NFT] Enhanced image URL for ${assetName}:`, betterImageUrl);
                                }
                            }
                        }).catch(() => {
                            // Silently fail - we already have a gateway URL
                        });
                    }
                } else {
                    imageUrl = ipfsMetadataToGateway(metadataText);
                }
            }
            
            // Add NFT if it's marked as NFT (has IPFS metadata) - imageUrl can be generated later
            // Include even if imageUrl is missing - it will be generated from metadata or fetched
            const nft: RavencoinNFT = {
                assetName,
                creator: cached?.creator ?? walletAddress,
                description: metadataText ? [metadataText] as [string] : [],
                imageUrl: imageUrl ? [imageUrl] as [string] : [],
            };
            console.log(`[NFT] Created NFT for ${assetName}:`, {
                hasImageUrl: !!imageUrl,
                imageUrl: imageUrl,
                hasMetadata: !!metadataText,
                isNFT: isNFT,
                hasIpfsMetadata: !!hasIpfsMetadata,
            });
            nftResults.push(nft);
        } else {
            assets.push({
                assetName,
                creator: cached?.creator ?? walletAddress,
                quantity,
                metadata: metadataText ? [metadataText] : [],
                isNFT: false,
            });
        }
    }

    // Second pass: fetch metadata in parallel for NFTs that need it (non-blocking)
    if (needsMetadataFetch.length > 0 && queryClient) {
        // Process in batches to avoid overwhelming the RPC
        const batchSize = 10;
        for (let i = 0; i < needsMetadataFetch.length; i += batchSize) {
            const batch = needsMetadataFetch.slice(i, i + batchSize);
            Promise.all(
                batch.map(async ({ assetName, quantityHint }) => {
                    try {
                        const rpcMeta = await fetchAssetMetadata(assetName);
                        if (rpcMeta?.ipfs_hash) {
                            const metadataText = `IPFS: ${rpcMeta.ipfs_hash}`;
                            const ipfsHash = rpcMeta.ipfs_hash;
                            
                            // Try to fetch IPFS content to see if it's JSON with an image URL
                            let imageUrl = `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
                            try {
                                const ipfsData = await fetchIpfsMetadata(ipfsHash);
                                if (ipfsData?.image || ipfsData?.image_url) {
                                    // Use the image URL from the JSON metadata
                                    imageUrl = ipfsData.image || ipfsData.image_url;
                                    console.log(`[NFT] Found image URL in IPFS JSON for ${assetName}:`, imageUrl);
                                } else {
                                    // Check if the IPFS content is a direct image
                                    const response = await fetch(`https://cloudflare-ipfs.com/ipfs/${ipfsHash}`, {
                                        method: 'HEAD',
                                    });
                                    const contentType = response.headers.get('content-type');
                                    if (contentType?.startsWith('image/')) {
                                        // It's a direct image, use the gateway URL
                                        imageUrl = `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`;
                                        console.log(`[NFT] IPFS hash is direct image for ${assetName}`);
                                    }
                                }
                            } catch (ipfsError) {
                                // Fallback to direct gateway URL
                                console.warn(`[NFT] Could not fetch IPFS content for ${assetName}, using direct gateway URL`);
                            }
                            
                            // Update the NFT in results - get current cache state
                            const currentCache = queryClient.getQueryData<RavencoinNFT[]>([
                                'ravencoinWalletNFTCache',
                                walletAddress,
                            ]) || nftResults;

                            const nftIndex = currentCache.findIndex((n) => n.assetName === assetName);
                            if (nftIndex >= 0) {
                                const updated = [...currentCache];
                                updated[nftIndex] = {
                                    ...updated[nftIndex],
                                    description: [metadataText],
                                    imageUrl: [imageUrl],
                                };
                                queryClient.setQueryData(['ravencoinWalletNFTCache', walletAddress], updated);
                                // Force immediate re-render
                                queryClient.invalidateQueries({ queryKey: ['ravencoinNFTs', walletAddress], exact: true });
                                console.log(`[NFT] Updated ${assetName} with imageUrl:`, imageUrl);
                            } else {
                                // If it wasn't classified as NFT before, add it now
                                const cached = cachedMap.get(assetName);
                                const rpcIsNFT = Number(rpcMeta.has_ipfs) === 1 && 
                                               (Number(rpcMeta.reissuable) === 0 || quantityHint);
                                if (rpcIsNFT) {
                                    const updated = [...currentCache, {
                                        assetName,
                                        creator: cached?.creator ?? walletAddress,
                                        description: [metadataText],
                                        imageUrl: [imageUrl],
                                    }];
                                    queryClient.setQueryData(['ravencoinWalletNFTCache', walletAddress], updated);
                                    queryClient.invalidateQueries({ queryKey: ['ravencoinNFTs', walletAddress], exact: true });
                                }
                            }
                        } else if (rpcMeta && !rpcMeta.ipfs_hash) {
                            // Asset exists but has no IPFS hash - NOT an NFT, remove from NFT list
                            const currentCache = queryClient.getQueryData<RavencoinNFT[]>([
                                'ravencoinWalletNFTCache',
                                walletAddress,
                            ]) || nftResults;
                            
                            const nftIndex = currentCache.findIndex(n => n.assetName === assetName);
                            if (nftIndex >= 0) {
                                // Remove from NFT list - it's not actually an NFT without IPFS metadata
                                const updated = currentCache.filter(n => n.assetName !== assetName);
                                queryClient.setQueryData(['ravencoinWalletNFTCache', walletAddress], updated);
                                queryClient.invalidateQueries({ queryKey: ['ravencoinNFTs', walletAddress], exact: true });
                                console.log(`[NFT] Removed ${assetName} from NFT list - no IPFS metadata`);
                            }
                        }
                    } catch (error) {
                        console.warn(`[NFT] Failed to fetch metadata for ${assetName}:`, error);
                    }
                })
            ).then(() => {
                // After each batch, force a re-render
                queryClient.invalidateQueries({ queryKey: ['ravencoinNFTs', walletAddress] });
            }).catch(() => {
                // Ignore batch errors
            });
        }
    }

    return { assets, nfts: nftResults };
}

// Helper to safely extract error messages - prevents binary data from being displayed
function getSafeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
    if (!error) return fallback;
    
    // Try to extract error from various formats
    let errorMsg: string | null = null;
    
    // Check for IC actor error format (AgentError)
    if (error.message && typeof error.message === 'string') {
        errorMsg = error.message;
    }
    // Check for IC rejection message
    else if (error.rejectMessage && typeof error.rejectMessage === 'string') {
        errorMsg = error.rejectMessage;
    }
    // Check for error.error (some APIs use this)
    else if (error.error && typeof error.error === 'string') {
        errorMsg = error.error;
    }
    // Check if error is a string itself
    else if (typeof error === 'string') {
        errorMsg = error;
    }
    // Check for IC error details
    else if (error.details && typeof error.details === 'string') {
        errorMsg = error.details;
    }
    // Check for error.toString()
    else if (error.toString && typeof error.toString === 'function') {
        try {
            const str = error.toString();
            if (str !== '[object Object]' && str !== 'Error') {
                errorMsg = str;
            }
        } catch {
            // Ignore toString errors
        }
    }
    
    // Validate error message (not binary data, not too long)
    if (errorMsg) {
        // Check if it looks like binary data (long string of numbers/commas)
        if (errorMsg.length > 500 || /^[\d,\s\[\]"]+$/.test(errorMsg)) {
            return fallback;
        }
        // Truncate very long messages but still show them
        if (errorMsg.length > 200) {
            return errorMsg.substring(0, 200) + '...';
        }
        return errorMsg;
    }
    
    // Last resort: try JSON.stringify for debugging (but limit length)
    try {
        const jsonStr = JSON.stringify(error);
        if (jsonStr && jsonStr.length < 200 && jsonStr !== '{}') {
            return jsonStr;
        }
    } catch {
        // Ignore JSON errors
    }
    
    return fallback;
}

// Helper function to create refetch intervals that stop on errors
function createRefetchInterval(intervalMs: number) {
    return (query: any) => {
        // Don't refetch if query is in error state
        if (query.state.error) {
            return false;
        }
        return intervalMs;
    };
}


export function useGetSupportedTokens() {
    const { actor, isFetching } = useActor();

    return useQuery<Array<[string, string]>>({
        queryKey: ['supportedTokens'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getSupportedTokens();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useGetFeeConfig() {
    const { actor, isFetching } = useActor();

    return useQuery<FeeConfig>({
        queryKey: ['feeConfig'],
        queryFn: async () => {
            if (!actor) {
                // Return default fee config instead of throwing
                return {
                    dexFeePercent: 0.3,
                    liquidityProviderRewardPercent: 0.2,
                };
            }
            return actor.getFeeConfig();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useUpdateFeeConfig() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (newConfig: FeeConfig) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to update fee configuration.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.updateFeeConfig(newConfig);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['feeConfig'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Fee Configuration Updated', {
                description: 'DEX and LP fees have been updated successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Fee Update Failed', {
                description: getSafeErrorMessage(error, 'Failed to update fee configuration.'),
                duration: 8000,
            });
        },
    });
}

export function useUpdateRavencoinConfirmationsConfig() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (newConfig: RavencoinConfirmationsConfig) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate to update confirmations policy.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.updateRavencoinConfirmationsConfig(newConfig);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinConfirmationsConfig'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Ravencoin confirmations updated', {
                description: 'Confirmation policy saved successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Update failed', {
                description: getSafeErrorMessage(error, 'Failed to update confirmations policy.'),
                duration: 8000,
            });
        },
    });
}

export function useCalculateSwapFees(amount: number | null) {
    const { actor, isFetching } = useActor();

    return useQuery<{
        dexFee: number;
        liquidityProviderReward: number;
        totalFees: number;
        finalAmount: number;
    }>({
        queryKey: ['swapFees', amount],
        queryFn: async () => {
            if (!actor || amount === null || amount <= 0) {
                return {
                    dexFee: 0,
                    liquidityProviderReward: 0,
                    totalFees: 0,
                    finalAmount: 0,
                };
            }
            return actor.calculateSwapFees(amount);
        },
        enabled: !!actor && !isFetching && amount !== null && amount > 0,
    });
}

export function useExecuteSwap() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ 
            fromToken, 
            toToken, 
            amount, 
            routedVia 
        }: { 
            fromToken: string; 
            toToken: string; 
            amount: number;
            routedVia?: string | null;
        }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to execute swaps.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            // Motoko optional Text parameters must be [] (None) or [string] (Some)
            // The IC agent expects array format, not null/undefined
            return actor.executeSwap(fromToken, toToken, amount, routedVia ? [routedVia] : []);
        },
        onSuccess: (_txId, variables) => {
            queryClient.invalidateQueries({ queryKey: ['transactionHistory'] });
            queryClient.invalidateQueries({ queryKey: ['feeBalance'] });
            queryClient.invalidateQueries({ queryKey: ['analytics'] });
            queryClient.invalidateQueries({ queryKey: ['publicAnalytics'] });
            
            toast.success('Swap Successful', {
                description: `Successfully swapped ${variables.amount} ${variables.fromToken} for ${variables.toToken}`,
                duration: 8000,
            });
        },
        onError: (error: any) => {
            toast.error('Swap Failed', {
                description: getSafeErrorMessage(error, 'Transaction failed. Please try again.'),
                duration: 8000,
            });
        },
    });
}

export function useGetTransactionHistory() {
    const { actor, isFetching } = useActor();

    return useQuery({
        queryKey: ['transactionHistory'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getTransactionHistory();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useGetCustomTokenPairs() {
    const { actor, isFetching } = useActor();

    return useQuery<CustomTokenPair[]>({
        queryKey: ['customTokenPairs'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getCustomTokenPairs();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useAddCustomTokenPair() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (pair: CustomTokenPair) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to add custom tokens.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.addCustomTokenPair(pair);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['customTokenPairs'] });
            toast.success('Token Pair Added', {
                description: `${variables.symbol} (${variables.name}) has been added successfully.`,
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Add Token', {
                description: getSafeErrorMessage(error, 'Could not add custom token pair.'),
                duration: 8000,
            });
        },
    });
}

export function useRemoveCustomTokenPair() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (symbol: string) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to remove tokens.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.removeCustomTokenPair(symbol);
        },
        onSuccess: (_, symbol) => {
            queryClient.invalidateQueries({ queryKey: ['customTokenPairs'] });
            toast.success('Token Pair Removed', {
                description: `${symbol} has been removed from your custom tokens.`,
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Remove Token', {
                description: getSafeErrorMessage(error, 'Could not remove custom token pair.'),
                duration: 8000,
            });
        },
    });
}

// Atomic Swap Hooks

export function useInitiateSwap() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ 
            participant, 
            amount, 
            secretHash, 
            timeLock 
        }: { 
            participant: Principal; 
            amount: number; 
            secretHash: Uint8Array; 
            timeLock: bigint;
        }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to initiate atomic swaps.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.initiateSwap(participant, amount, secretHash, timeLock);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myAtomicSwaps'] });
            toast.success('Atomic Swap Initiated', {
                description: 'Your atomic swap has been initiated successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Swap Initiation Failed', {
                description: getSafeErrorMessage(error, 'Failed to initiate atomic swap.'),
                duration: 8000,
            });
        },
    });
}

export function useParticipateSwap() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ 
            swapId, 
            amount, 
            secretHash, 
            timeLock 
        }: { 
            swapId: bigint; 
            amount: number; 
            secretHash: Uint8Array; 
            timeLock: bigint;
        }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to participate in swaps.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.participateSwap(swapId, amount, secretHash, timeLock);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myAtomicSwaps'] });
            queryClient.invalidateQueries({ queryKey: ['atomicSwap'] });
            toast.success('Swap Participation Confirmed', {
                description: 'You have successfully participated in the atomic swap.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Participation Failed', {
                description: getSafeErrorMessage(error, 'Failed to participate in atomic swap.'),
                duration: 8000,
            });
        },
    });
}

export function useRedeemSwap() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ swapId, secret }: { swapId: bigint; secret: Uint8Array }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to redeem swaps.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.redeemSwap(swapId, secret);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myAtomicSwaps'] });
            queryClient.invalidateQueries({ queryKey: ['atomicSwap'] });
            toast.success('Swap Redeemed', {
                description: 'Your atomic swap has been redeemed successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Redemption Failed', {
                description: getSafeErrorMessage(error, 'Failed to redeem atomic swap.'),
                duration: 8000,
            });
        },
    });
}

export function useRefundSwap() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (swapId: bigint) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to refund swaps.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.refundSwap(swapId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myAtomicSwaps'] });
            queryClient.invalidateQueries({ queryKey: ['atomicSwap'] });
            toast.success('Swap Refunded', {
                description: 'Your atomic swap has been refunded successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Refund Failed', {
                description: getSafeErrorMessage(error, 'Failed to refund atomic swap.'),
                duration: 8000,
            });
        },
    });
}

export function useGetAtomicSwap(swapId: bigint | null) {
    const { actor, isFetching } = useActor();

    return useQuery<HTLC | null>({
        queryKey: ['atomicSwap', swapId?.toString()],
        queryFn: async () => {
            if (!actor || swapId === null) return null;
            return actor.getAtomicSwap(swapId);
        },
        enabled: !!actor && !isFetching && swapId !== null,
    });
}

export function useGetMyAtomicSwaps() {
    const { actor, isFetching } = useActor();

    return useQuery<HTLC[]>({
        queryKey: ['myAtomicSwaps'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getMyAtomicSwaps();
        },
        enabled: !!actor && !isFetching,
    });
}

// Swap Offer Hooks

export function useGetAvailableSwapOffers() {
    const { actor, isFetching } = useActor();

    return useQuery<SwapOffer[]>({
        queryKey: ['availableSwapOffers'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAvailableSwapOffers();
        },
        enabled: !!actor && !isFetching,
        refetchInterval: false, // DISABLED - was causing spam
    });
}

export function useAcceptSwapOffer() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (offerId: bigint) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to accept swap offers.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.acceptSwapOffer(offerId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availableSwapOffers'] });
            toast.success('Offer Accepted', {
                description: 'You have successfully accepted the swap offer.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Accept Offer', {
                description: getSafeErrorMessage(error, 'Could not accept swap offer.'),
                duration: 8000,
            });
        },
    });
}

export function useCreateSwapOffer() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ 
            fromToken, 
            toToken, 
            fromAmount, 
            toAmount, 
            expiration 
        }: { 
            fromToken: string; 
            toToken: string; 
            fromAmount: number; 
            toAmount: number; 
            expiration: bigint;
        }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to create swap offers.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.createSwapOffer(fromToken, toToken, fromAmount, toAmount, expiration);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availableSwapOffers'] });
            queryClient.invalidateQueries({ queryKey: ['mySwapOffers'] });
            toast.success('Swap Offer Created', {
                description: 'Your swap offer has been created and is now available.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Create Offer', {
                description: getSafeErrorMessage(error, 'Could not create swap offer.'),
                duration: 8000,
            });
        },
    });
}

export function useGetMySwapOffers() {
    const { actor, isFetching } = useActor();

    return useQuery<SwapOffer[]>({
        queryKey: ['mySwapOffers'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getMySwapOffers();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useCancelSwapOffer() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (offerId: bigint) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity to cancel offers.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.cancelSwapOffer(offerId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availableSwapOffers'] });
            queryClient.invalidateQueries({ queryKey: ['mySwapOffers'] });
            toast.success('Offer Cancelled', {
                description: 'Your swap offer has been cancelled.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Cancel Offer', {
                description: getSafeErrorMessage(error, 'Could not cancel swap offer.'),
                duration: 8000,
            });
        },
    });
}

// Admin Hooks with defensive waiting for IC identity resolution

export function useIsCallerAdmin() {
    const { actor, isFetching } = useActor();
    const { isConnected, walletAddress } = useWallet();

    return useQuery<boolean>({
        queryKey: ['isCallerAdmin'],
        queryFn: async () => {
            if (!actor) {
                return false;
            }
            try {
                // Check if actor has the method
                if (typeof actor.isCallerAdmin !== 'function') {
                    console.error('useIsCallerAdmin: actor.isCallerAdmin is not a function', actor);
                    return false;
                }
                // No longer check for anonymous - backend allows it now for wallet-based admin
                const result = await actor.isCallerAdmin();
                return result;
            } catch (error) {
                console.error('Failed to check admin status:', error);
                // Don't throw - return false instead
                return false;
            }
        },
        // Only run query if wallet is actually connected - prevents false admin status on page load
        enabled: !!actor && !isFetching && isConnected && !!walletAddress,
        refetchInterval: false, // DISABLED - was causing spam
        refetchOnMount: false, // Disabled to reduce CPU - only refetch when explicitly needed
        retry: false, // Don't retry on error
        retryDelay: 1000,
        // Set initial data to false to prevent showing admin on page load
        initialData: false,
        placeholderData: false,
        staleTime: 120000, // Consider data fresh for 2 minutes (increased from 60s) to reduce queries
        // Errors are handled inside queryFn to avoid unhandled states
    });
}

export function useIsCallerTemporaryAdmin() {
    const { actor, isFetching } = useActor();

    return useQuery<boolean>({
        queryKey: ['isCallerTemporaryAdmin'],
        queryFn: async () => {
            if (!actor) {
                return false;
            }
            
            // No longer check for anonymous - backend allows it now
            try {
                if (typeof actor.isCallerTemporaryAdmin !== 'function') {
                    console.error('useIsCallerTemporaryAdmin: actor.isCallerTemporaryAdmin is not a function', actor);
                    return false;
                }
                const result = await actor.isCallerTemporaryAdmin();
                return result;
            } catch (error) {
                console.error('Failed to check temporary admin status:', error);
                return false;
            }
        },
        // CRITICAL: Enable for passcode-based admin (no wallet required)
        // Also enable if wallet is connected for wallet-based admin checks
        // This allows temporary admin access via passcode without wallet connection
        enabled: !!actor && !isFetching,
        refetchInterval: false,
        refetchOnMount: true, // Enable to check admin status after passcode validation
        retry: false,
        // Set initial data to false to prevent showing admin on page load
        initialData: false,
        placeholderData: false,
        staleTime: 0, // Always refetch to get latest admin status
        // Errors are handled inside queryFn to avoid unhandled states
    });
}

// Admin-protected queries that work with both wallet-based admin AND temporary admin
export function useGetFeeBalance() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    // Wait for both admin checks to complete
    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<number>({
        queryKey: ['feeBalance'],
        queryFn: async () => {
            if (!actor) return 0; // Return 0 instead of throwing
            return actor.getFeeBalance();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: false, // DISABLED - was causing spam
        retry: false,
    });
}

export function useWithdrawFees() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to withdraw fees.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.withdrawFees();
        },
        onSuccess: (message) => {
            queryClient.invalidateQueries({ queryKey: ['feeBalance'] });
            queryClient.invalidateQueries({ queryKey: ['transactionHistory'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Fees Withdrawn', {
                description: message || 'DEX fees have been withdrawn successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Withdrawal Failed', {
                description: getSafeErrorMessage(error, 'Failed to withdraw fees.'),
                duration: 8000,
            });
        },
    });
}

export function useGetAdminWalletAddress() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<string>({
        queryKey: ['adminWalletAddress'],
        queryFn: async () => {
            if (!actor) return '';
            return actor.getAdminWalletAddress();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        retry: false,
    });
}

export function useIsAdminWalletAddress(walletAddress: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<boolean>({
        queryKey: ['isAdminWalletAddress', walletAddress],
        queryFn: async () => {
            if (!actor || !walletAddress) return false;
            return actor.isAdminWalletAddress(walletAddress);
        },
        enabled: !!actor && !isFetching && !!walletAddress,
    });
}

export function useGetAnalytics() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<AnalyticsData>({
        queryKey: ['analytics'],
        queryFn: async () => {
            if (!actor) {
                // Return default analytics instead of throwing
                return {
                    totalSwapCount: BigInt(0),
                    totalVolume: 0,
                    dailyTrades: BigInt(0),
                    weeklyTrades: BigInt(0),
                };
            }
            return actor.getAnalytics();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: false, // DISABLED - was causing spam
        retry: false,
    });
}

// Public Analytics Hook - No authentication required, returns limited data
export function useGetPublicAnalytics() {
    const { actor, isFetching } = useActor();

    return useQuery<{ totalSwapCount: bigint; onChainVolume: number; thirdPartyVolume: number }>({
        queryKey: ['publicAnalytics'],
        queryFn: async () => {
            if (!actor) {
                return {
                    totalSwapCount: BigInt(0),
                    onChainVolume: 0,
                    thirdPartyVolume: 0,
                };
            }
            try {
                const analytics = await actor.getPublicAnalytics();
                // Ensure all fields are present
                return {
                    totalSwapCount: analytics.totalSwapCount || BigInt(0),
                    onChainVolume: (analytics as any).onChainVolume || 0,
                    thirdPartyVolume: (analytics as any).thirdPartyVolume || 0,
                };
            } catch (error: any) {
                console.error('Failed to fetch public analytics:', error);
                return {
                    totalSwapCount: BigInt(0),
                    onChainVolume: 0,
                    thirdPartyVolume: 0,
                };
            }
        },
        enabled: !!actor && !isFetching,
        refetchInterval: false, // DISABLED - was causing spam
        retry: 2,
    });
}

export function useGetAllSwapOffers() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<Array<[bigint, SwapOffer]>>({
        queryKey: ['allSwapOffers'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAllSwapOffers();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        retry: false,
    });
}

// Admin Activity Log Hook
export function useGetAdminActivityLog() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<AdminActivity[]>({
        queryKey: ['adminActivityLog'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAdminActivityLog();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: false, // DISABLED - was causing spam
        retry: false,
    });
}

// On-Chain Audit Feed Hook
export function useGetOnChainAuditFeed() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<OnChainAuditEntry[]>({
        queryKey: ['onChainAuditFeed'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getOnChainAuditFeed();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: false, // DISABLED - was causing spam
        retry: false,
    });
}

// Ravencoin Asset & NFT Hooks - User-specific (requires wallet connection)
export function useGetRavencoinAssets(walletAddress: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinAsset[]>({
        queryKey: ['ravencoinAssets', walletAddress],
        queryFn: async () => {
            if (!walletAddress) return [];

            // Add timeout wrapper
            const timeoutPromise = new Promise<RavencoinAsset[]>((_, reject) => {
                setTimeout(() => reject(new Error('Query timeout after 15 seconds')), 15000);
            });

            const queryPromise = (async () => {
                try {
                    console.log(`[RVN Assets] ========== FETCHING ASSETS (NO CACHE) ==========`);
                    console.log(`[RVN Assets] Wallet: ${walletAddress}`);
                    
                    // Fetch balances directly from RPC - NO CACHE, NO CLASSIFICATION
                    const balances = await fetchWalletAssetBalances(walletAddress);
                    
                    const assets: RavencoinAsset[] = [];
                    for (const [assetName, quantity] of Object.entries(balances)) {
                        assets.push({
                            assetName,
                            creator: walletAddress,
                            quantity,
                            metadata: [],
                            isNFT: false, // Always false - NFTs are handled by useGetRavencoinNFTs
                        });
                    }
                    
                    console.log(`[RVN Assets] ✅ Direct RPC returned ${assets.length} assets (NFTs excluded)`);
                    return assets;
                } catch (error) {
                    console.error('[RVN Assets] ❌ Direct RPC failed, trying backend:', error);
                    // Fallback to backend only if RPC fails
                    if (actor) {
                        try {
                            const backendAssets = await actor.getRavencoinWalletAssets(walletAddress, false);
                            console.log(`[RVN Assets] ✅ Backend returned ${backendAssets.length} assets`);
                            return backendAssets;
                        } catch (backendError) {
                            console.error('[RVN Assets] ❌ Backend also failed:', backendError);
                        }
                    }
                    return [];
                }
            })();

            return Promise.race([queryPromise, timeoutPromise]);
        },
        enabled: !isFetching && !!walletAddress,
        retry: 1,
        retryDelay: 1000,
        staleTime: 30000, // 30 seconds
        gcTime: 300000, // 5 minutes
    });
}

export function useGetRavencoinNFTs(walletAddress: string | null) {
    const queryClient = useQueryClient();
    
    console.log(`[RVN NFT HOOK] ========== HOOK CALLED ==========`);
    console.log(`[RVN NFT HOOK] walletAddress: ${walletAddress}`);
    console.log(`[RVN NFT HOOK] enabled: ${!!walletAddress}`);

    return useQuery<RavencoinNFT[]>({
        queryKey: ['ravencoinNFTs', walletAddress],
        queryFn: async () => {
            console.log(`[RVN NFT HOOK] ========== QUERY FN CALLED ==========`);
            console.log(`[RVN NFT HOOK] walletAddress in queryFn: ${walletAddress}`);
            if (!walletAddress) {
                console.log(`[RVN NFT HOOK] No wallet address, returning empty`);
                return [];
            }

            // Direct RPC fetch (bypasses cache completely, like Ethereum)
            // This ensures we always get fresh data and proper NFT filtering
            try {
                console.log(`[RVN NFT] ========== FETCHING NFTs (NO CACHE) ==========`);
                console.log(`[RVN NFT] Wallet: ${walletAddress}`);
                const nfts = await fetchRavencoinNFTsDirectRPC(walletAddress);
                console.log(`[RVN NFT] ✅✅✅ Direct RPC returned ${nfts.length} NFTs ✅✅✅`);
                
                // Log ALL NFTs for debugging
                if (nfts.length > 0) {
                    console.log(`[RVN NFT] ========== NFT LIST ==========`);
                    nfts.forEach((nft, idx) => {
                        console.log(`[RVN NFT] ${idx + 1}. ${nft.assetName}`);
                        console.log(`[RVN NFT]    Creator: ${nft.creator}`);
                        console.log(`[RVN NFT]    Description: ${nft.description?.[0] ?? 'NONE'}`);
                        console.log(`[RVN NFT]    ImageUrl: ${nft.imageUrl?.[0] ?? 'NONE'}`);
                    });
                    console.log(`[RVN NFT] ============================`);
                } else {
                    console.warn(`[RVN NFT] ⚠️⚠️⚠️ NO NFTs FOUND ⚠️⚠️⚠️`);
                }
                
                // Cache the results for quick subsequent access
                // Cast to RavencoinNFT[] since the structure matches exactly
                queryClient.setQueryData(['ravencoinWalletNFTCache', walletAddress], nfts as RavencoinNFT[]);
                
                return nfts as RavencoinNFT[];
            } catch (error) {
                console.error('[RVN NFT] ❌❌❌ Direct RPC fetch failed:', error);
                console.error('[RVN NFT] Error details:', JSON.stringify(error, null, 2));
                // Return empty array on error (don't show stale data)
                return [];
            }
        },
        enabled: !!walletAddress,
        staleTime: 60000, // 1 minute - data is fresh enough
        gcTime: 300000, // 5 minutes - keep in cache for quick access
        retry: 2, // Retry twice on failure
        retryDelay: 1000, // Wait 1 second between retries
    });
}

// Public Ravencoin Asset Hooks - No authentication required, with automatic refresh
export function useGetAllRavencoinAssets() {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinAsset[]>({
        queryKey: ['allRavencoinAssets'],
        queryFn: async () => {
            if (!actor) return [];
            try {
                const pageSize = 1000;
                let page = 0;
                let allAssets: RavencoinAsset[] = [];
                while (true) {
                    const pageResult = await actor.getRavencoinAssetsPaginated(page, pageSize);
                    if (!pageResult.assets || pageResult.assets.length === 0) break;
                    allAssets = allAssets.concat(pageResult.assets);
                    if (pageResult.currentPage + BigInt(1) >= pageResult.totalPages) {
                        break;
                    }
                    page += 1;
                }
                return allAssets;
            } catch (error: any) {
                console.error('Failed to fetch Ravencoin assets:', error);
                return [];
            }
        },
        enabled: !!actor && !isFetching,
        refetchInterval: false, // DISABLED - was causing spam // Auto-refresh every 30 seconds
        staleTime: 25000, // Consider data stale after 25 seconds
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    });
}

export function useGetRavencoinAssetsPaginated(page: number, limit: number) {
    const { actor, isFetching } = useActor();

    return useQuery<{
        assets: RavencoinAsset[];
        totalCount: bigint;
        currentPage: bigint;
        totalPages: bigint;
    }>({
        queryKey: ['ravencoinAssetsPaginated', page, limit],
        queryFn: async () => {
            if (!actor) {
                return {
                    assets: [],
                    totalCount: BigInt(0),
                    currentPage: BigInt(0),
                    totalPages: BigInt(0),
                };
            }
            try {
                return await actor.getRavencoinAssetsPaginated(BigInt(page), BigInt(limit));
            } catch (error: any) {
                console.error('Failed to fetch paginated assets:', error);
                return {
                    assets: [],
                    totalCount: BigInt(0),
                    currentPage: BigInt(0),
                    totalPages: BigInt(0),
                };
            }
        },
        enabled: !!actor && !isFetching,
        retry: 2,
    });
}

export function useSearchRavencoinAssets(searchTerm: string) {
    const { actor, isFetching } = useActor();
    const queryClient = useQueryClient();
    const normalizedTerm = searchTerm.trim();
    const meetsMinLength = normalizedTerm.length >= 3;

    return useQuery<RavencoinAsset[]>({
        queryKey: ['searchRavencoinAssets', normalizedTerm],
        queryFn: async () => {
            if (!meetsMinLength) return [];

            if (actor) {
                try {
                    const results = await actor.searchRavencoinAssets(normalizedTerm);
                    if (results.length > 0) {
                        return results;
                    }
                } catch (error: any) {
                    console.warn('Asset search failed, falling back to cached data:', error);
                }
            }

            const cachedAssets = queryClient.getQueryData<RavencoinAsset[]>(['allRavencoinAssets']) ?? [];
            const lower = normalizedTerm.toLowerCase();
            return cachedAssets
                .filter((asset) => {
                    const metadataText = unpackMetadata(asset.metadata);
                    return (
                        asset.assetName.toLowerCase().includes(lower) ||
                        asset.creator.toLowerCase().includes(lower) ||
                        (metadataText?.toLowerCase().includes(lower) ?? false)
                    );
                })
                .slice(0, 100);
        },
        enabled: !isFetching && meetsMinLength,
        retry: 1,
    });
}

export function useGetRavencoinAssetLeaderboard() {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinAsset[]>({
        queryKey: ['ravencoinAssetLeaderboard'],
        queryFn: async () => {
            if (!actor) return [];
            try {
                return await actor.getRavencoinAssetLeaderboard();
            } catch (error: any) {
                console.error('Failed to fetch asset leaderboard:', error);
                return [];
            }
        },
        enabled: !!actor && !isFetching,
        refetchInterval: false, // DISABLED - was causing spam // Auto-refresh every 30 seconds
        staleTime: 25000,
        retry: 2,
    });
}

// New hook to get asset sync status with live feedback
export function useGetAssetSyncStatus() {
    const { actor, isFetching } = useActor();

    return useQuery<{
        lastSyncTimestamp: bigint;
        assetCount: bigint;
        syncInProgress: boolean;
        retryCount: bigint;
    }>({
        queryKey: ['assetSyncStatus'],
        queryFn: async () => {
            if (!actor) {
                return {
                    lastSyncTimestamp: BigInt(0),
                    assetCount: BigInt(0),
                    syncInProgress: false,
                    retryCount: BigInt(0),
                };
            }
            try {
                return await actor.getAssetSyncStatus();
            } catch (error: any) {
                console.error('Failed to fetch asset sync status:', error);
                return {
                    lastSyncTimestamp: BigInt(0),
                    assetCount: BigInt(0),
                    syncInProgress: false,
                    retryCount: BigInt(0),
                };
            }
        },
        enabled: !!actor && !isFetching,
        refetchInterval: false, // DISABLED - was causing spam
        retry: 1,
    });
}

// Admin-only asset sync hook with comprehensive feedback and automatic retry until data arrives
export function useSyncRavencoinAssets() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to sync assets.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            
            // Use frontend-based sync (bypasses IC HTTP outcall limit)
            const { syncAssetsViaFrontend } = await import('../lib/assetSync');
            return await syncAssetsViaFrontend(actor);
        },
        onSuccess: (message) => {
            // Invalidate all asset-related queries to trigger refresh
            queryClient.invalidateQueries({ queryKey: ['allRavencoinAssets'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinAssetsPaginated'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinAssetLeaderboard'] });
            queryClient.invalidateQueries({ queryKey: ['searchRavencoinAssets'] });
            queryClient.invalidateQueries({ queryKey: ['assetSyncStatus'] });
            
            // Don't show success/warning toasts - sync happens silently in background
            // Users don't need to see asset counts or sync status messages
            // Only log to console for debugging
            const isSuccess = message.includes('completed successfully');
            const assetCount = message.match(/Total assets: (\d+)/)?.[1] || '0';
            console.log(`[Asset Sync] ${isSuccess ? 'Success' : 'Partial'}: ${assetCount} assets synced`);
        },
        onError: (error: any) => {
            // Use safe error message extraction to prevent binary data display
            const errorMessage = getSafeErrorMessage(error, 'Unknown error occurred');
            
            // Log the full error for debugging
            console.error('[Asset Sync] Full error:', error);
            console.warn('[Asset Sync] Extracted message:', errorMessage);
            
            // Only show error toasts for manual syncs, not auto-syncs
            // Auto-sync errors are handled silently in the component
            // Check if this is a manual sync by checking if onError was called from component
            // For now, we'll reduce toast frequency and only show critical errors
            
            // Determine error type
            const isRateLimit = errorMessage.includes('30 seconds') || errorMessage.includes('sync already in progress');
            const isAuthError = errorMessage.includes('Unauthorized') || errorMessage.includes('admin') || errorMessage.includes('authentication');
            // Connection/parsing errors are logged but not shown to avoid toast spam
            
            // Show toast for authentication errors (user needs to know they need to authenticate)
            if (isAuthError) {
                console.warn('[Asset Sync] Authentication required - user needs to authenticate as admin');
                // Don't show toast for auto-sync auth errors - too noisy
                // User will see this when they try to manually sync
            }
            // Only show toasts for rate limits (user action needed) - suppress others to prevent spam
            else if (isRateLimit) {
                toast.warning('Sync Rate Limited', {
                    description: 'Asset sync is rate-limited to once every 30 seconds. Please wait before trying again.',
                    duration: 5000,
                });
            }
            // Log other errors silently to console instead of showing toasts
            // This prevents endless error popups from auto-sync failures
            else {
                console.warn('[Asset Sync] Error (silent):', errorMessage);
            }
        },
    });
}

// ============================================
// Peer-to-Peer Order Book Hooks
// ============================================

type CreateRavencoinOrderPayload = {
    orderType: RavencoinOrderType;
    assetName: string;
    assetAmount: number;
    rvnAmount: number;
    walletAddress: string;
    expirationMinutes: number;
    secretHash?: Uint8Array; // Optional: Secret hash for HTLC (required for sell orders)
};

type CreateRavencoinOrderManualPayload = CreateRavencoinOrderPayload & {
    accessCode: string;
};

export function useCreateRavencoinOrder() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CreateRavencoinOrderPayload) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before creating an order.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }

            // Convert secretHash to Blob if provided
            const secretHashBlob = payload.secretHash 
                ? Array.from(payload.secretHash) 
                : undefined;

            return actor.createRavencoinOrder(
                payload.orderType,
                payload.assetName,
                payload.assetAmount,
                payload.rvnAmount,
                payload.walletAddress,
                BigInt(payload.expirationMinutes),
                secretHashBlob ? [secretHashBlob] : []
            );
        },
        onSuccess: (_orderId) => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['myRavencoinOrders'] });
            toast.success('Order created', {
                description: `Your order has been created and will be matched automatically.`,
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Order creation failed', {
                description: getSafeErrorMessage(error, 'Unable to create order. Please try again.'),
                duration: 8000,
            });
        },
    });
}

export function useCreateRavencoinOrderManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CreateRavencoinOrderManualPayload) => {
            if (!actor) {
                toast.error('Backend Unavailable', {
                    description: 'Please try again in a moment.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }

            const secretHashBlob = payload.secretHash
                ? Array.from(payload.secretHash)
                : undefined;

            return actor.createRavencoinOrderManual(
                payload.orderType,
                payload.assetName,
                payload.assetAmount,
                payload.rvnAmount,
                payload.walletAddress,
                BigInt(payload.expirationMinutes),
                secretHashBlob ? [secretHashBlob] : [],
                payload.accessCode
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
        },
        onError: (error: any) => {
            toast.error('Order creation failed', {
                description: getSafeErrorMessage(error, 'Unable to create order. Please try again.'),
                duration: 8000,
            });
        },
    });
}

export function useCancelRavencoinOrder() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (orderId: number | bigint) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before cancelling an order.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.cancelRavencoinOrder(BigInt(orderId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['myRavencoinOrders'] });
            toast.success('Order cancelled', {
                description: 'The order has been cancelled.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Cancellation failed', {
                description: getSafeErrorMessage(error, 'Unable to cancel order.'),
                duration: 8000,
            });
        },
    });
}

export function useCancelRavencoinOrderManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ orderId, accessCode }: { orderId: number | bigint; accessCode: string }) => {
            if (!actor) {
                toast.error('Backend Unavailable', {
                    description: 'Please try again in a moment.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.cancelRavencoinOrderManual(BigInt(orderId), accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
        },
        onError: (error: any) => {
            toast.error('Cancellation failed', {
                description: getSafeErrorMessage(error, 'Unable to cancel order.'),
                duration: 8000,
            });
        },
    });
}

export function useGetRavencoinOrderBook(assetName?: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<Array<[bigint, RavencoinOrder]>>({
        queryKey: ['ravencoinOrderBook', assetName],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getRavencoinOrderBook(assetName ? [assetName] : []);
        },
        enabled: !!actor && !isFetching,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

export function useGetRavencoinOrderDepth(assetName: string, depthLevels: number = 10) {
    const { actor, isFetching } = useActor();

    return useQuery<{ buyDepth: OrderDepth[]; sellDepth: OrderDepth[] }>({
        queryKey: ['ravencoinOrderDepth', assetName, depthLevels],
        queryFn: async () => {
            if (!actor) return { buyDepth: [], sellDepth: [] };
            return actor.getRavencoinOrderDepth(assetName, BigInt(depthLevels));
        },
        enabled: !!actor && !isFetching && !!assetName,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

export function useGetMyRavencoinOrders() {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinOrder[]>({
        queryKey: ['myRavencoinOrders'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getMyRavencoinOrders();
        },
        enabled: !!actor && !isFetching,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

export function useGetRavencoinOrdersByWalletAddress(walletAddress: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinOrder[]>({
        queryKey: ['ravencoinOrdersByWallet', walletAddress],
        queryFn: async () => {
            if (!actor || !walletAddress) return [];
            return actor.getRavencoinOrdersByWalletAddress(walletAddress);
        },
        enabled: !!actor && !isFetching && !!walletAddress,
        refetchInterval: createRefetchInterval(30000),
    });
}

export function useGetAllRavencoinOrders() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<RavencoinOrder[]>({
        queryKey: ['allRavencoinOrders'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAllRavencoinOrders();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

// Public: Get asset names with active orders (for filtering UI)
export function useGetAssetsWithActiveOrders() {
    const { actor, isFetching } = useActor();

    return useQuery<string[]>({
        queryKey: ['assetsWithActiveOrders'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAssetsWithActiveOrders();
        },
        enabled: !!actor && !isFetching,
        refetchInterval: createRefetchInterval(30000), // Refresh every 30s to stay current
    });
}

export function useGetAllRavencoinHTLCs() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<RavencoinHTLC[]>({
        queryKey: ['allRavencoinHTLCs'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAllRavencoinHTLCs();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

export function useGetRavencoinHTLCsByWalletAddress(walletAddress: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinHTLC[]>({
        queryKey: ['ravencoinHTLCsByWallet', walletAddress],
        queryFn: async () => {
            if (!actor || !walletAddress) return [];
            return actor.getRavencoinHTLCsByWalletAddress(walletAddress);
        },
        enabled: !!actor && !isFetching && !!walletAddress,
        refetchInterval: createRefetchInterval(30000),
    });
}

// Escrow System Hooks
export function useGetPlatformRavencoinAddress() {
    const { actor, isFetching } = useActor();
    
    return useQuery<string>({
        queryKey: ['platformRavencoinAddress'],
        queryFn: async () => {
            if (!actor) return '';
            return actor.getPlatformRavencoinAddress();
        },
        enabled: !!actor && !isFetching,
        staleTime: Infinity, // Address doesn't change
    });
}

export function useCalculateRavencoinFee(rvnAmount: number) {
    const { actor, isFetching } = useActor();
    
    return useQuery<number>({
        queryKey: ['ravencoinFee', rvnAmount],
        queryFn: async () => {
            if (!actor || rvnAmount <= 0) return 0;
            return actor.calculateRavencoinFee(rvnAmount);
        },
        enabled: !!actor && !isFetching && rvnAmount > 0,
    });
}

export function useGetRavencoinConfirmationsConfig() {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinConfirmationsConfig>({
        queryKey: ['ravencoinConfirmationsConfig'],
        queryFn: async () => {
            if (!actor) {
                return {
                    defaultConfirmations: 2n,
                    highValueConfirmations: 5n,
                    highValueUsd: 250,
                };
            }
            return actor.getRavencoinConfirmationsConfig();
        },
        enabled: !!actor && !isFetching,
        staleTime: 300000,
    });
}

export function useGetRavencoinTxConfirmations(txId: string | null) {
    const { actor, isFetching } = useActor();

    return useQuery<bigint>({
        queryKey: ['ravencoinTxConfirmations', txId],
        queryFn: async () => {
            if (!actor || !txId) return 0n;
            return actor.getRavencoinTxConfirmations(txId);
        },
        enabled: !!actor && !isFetching && !!txId,
        refetchInterval: createRefetchInterval(30000),
    });
}

export function useGetRavencoinHTLCRequiredConfirmations(htlcId: bigint | number | null) {
    const { actor, isFetching } = useActor();

    return useQuery<bigint>({
        queryKey: ['ravencoinHTLCRequiredConfirmations', htlcId?.toString()],
        queryFn: async () => {
            if (!actor || htlcId == null) return 0n;
            const id = typeof htlcId === 'bigint' ? htlcId : BigInt(htlcId);
            return actor.getRavencoinHTLCRequiredConfirmations(id);
        },
        enabled: !!actor && !isFetching && htlcId != null,
        refetchInterval: createRefetchInterval(60000),
    });
}

export function useRefreshRavencoinPriceUsd() {
    const { actor } = useActor();

    return useMutation({
        mutationFn: async () => {
            if (!actor) throw new Error('Backend not initialized');
            return actor.refreshRavencoinPriceUsd();
        },
    });
}

export function useGetEscrowDepositInstructions(orderId: number | null) {
    const { actor, isFetching } = useActor();
    
    return useQuery({
        queryKey: ['escrowDepositInstructions', orderId],
        queryFn: async () => {
            if (!actor || !orderId) return null;
            return actor.getEscrowDepositInstructions(orderId);
        },
        enabled: !!actor && !isFetching && !!orderId,
    });
}

export function useGetEscrowDepositStatus(orderId: number | null) {
    const { actor, isFetching } = useActor();
    
    return useQuery({
        queryKey: ['escrowDepositStatus', orderId],
        queryFn: async () => {
            if (!actor || !orderId) return null;
            return actor.getEscrowDepositStatus(orderId);
        },
        enabled: !!actor && !isFetching && !!orderId,
        refetchInterval: createRefetchInterval(5000), // Check every 5s for verification
    });
}

export function useSubmitRavencoinEscrowDeposit() {
    const { actor } = useActor();
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: async ({ orderId, assetTxId, feeTxId }: { orderId: number; assetTxId: string; feeTxId: string }) => {
            if (!actor) throw new Error('Backend not initialized');
            return actor.submitRavencoinEscrowDeposit(orderId, assetTxId, feeTxId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['escrowDepositStatus'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['myRavencoinOrders'] });
        },
    });
}

export function useSubmitRavencoinEscrowDepositManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            orderId,
            assetTxId,
            feeTxId,
            accessCode,
        }: {
            orderId: number;
            assetTxId: string;
            feeTxId: string;
            accessCode: string;
        }) => {
            if (!actor) throw new Error('Backend not initialized');
            return actor.submitRavencoinEscrowDepositManual(orderId, assetTxId, feeTxId, accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['escrowDepositStatus'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrderBook'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
        },
    });
}

export function useRequestRavencoinEscrowRefund() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ orderId, accessCode }: { orderId: number; accessCode: string }) => {
            if (!actor) throw new Error('Backend not initialized');
            return actor.requestRavencoinEscrowRefund(orderId, accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['escrowDepositStatus'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
        },
        onError: (error: any) => {
            toast.error('Refund failed', {
                description: getSafeErrorMessage(error, 'Unable to process escrow refund.'),
                duration: 8000,
            });
        },
    });
}

export function useGetRavencoinOrder(orderId: bigint | null) {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinOrder | null>({
        queryKey: ['ravencoinOrder', orderId?.toString()],
        queryFn: async () => {
            if (!actor || !orderId) return null;
            return actor.getRavencoinOrder(orderId) || null;
        },
        enabled: !!actor && !isFetching && orderId !== null,
    });
}

export function useGetRavencoinHTLC(htlcId: bigint | null) {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinHTLC | null>({
        queryKey: ['ravencoinHTLC', htlcId?.toString()],
        queryFn: async () => {
            if (!actor || !htlcId) return null;
            return actor.getRavencoinHTLC(htlcId) || null;
        },
        enabled: !!actor && !isFetching && htlcId !== null,
    });
}

export function useGetMyRavencoinHTLCs() {
    const { actor, isFetching } = useActor();

    return useQuery<RavencoinHTLC[]>({
        queryKey: ['myRavencoinHTLCs'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getMyRavencoinHTLCs();
        },
        enabled: !!actor && !isFetching,
        refetchInterval: createRefetchInterval(30000), // Reduced from 10s to 30s
    });
}

export function useParticipateRavencoinHTLC() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ htlcId, txId }: { htlcId: number | bigint; txId: string }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before participating in HTLC.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.participateRavencoinHTLC(BigInt(htlcId), txId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            toast.success('Funds locked', {
                description: 'Your funds have been locked in the HTLC.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to lock funds', {
                description: getSafeErrorMessage(error, 'Unable to lock funds in HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useParticipateRavencoinHTLCManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ htlcId, txId, accessCode }: { htlcId: number | bigint; txId: string; accessCode: string }) => {
            if (!actor) {
                toast.error('Backend Unavailable', {
                    description: 'Please try again in a moment.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.participateRavencoinHTLCManual(BigInt(htlcId), txId, accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            toast.success('Funds locked', {
                description: 'Your funds have been locked in the HTLC.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to lock funds', {
                description: getSafeErrorMessage(error, 'Unable to lock funds in HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useRedeemRavencoinHTLC() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ htlcId, secret, initiatorTxId }: { htlcId: number | bigint; secret: Uint8Array; initiatorTxId: string }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before redeeming HTLC.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.redeemRavencoinHTLC(BigInt(htlcId), secret, initiatorTxId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            queryClient.invalidateQueries({ queryKey: ['myRavencoinOrders'] });
            toast.success('Swap completed', {
                description: 'The HTLC has been redeemed and swap is complete.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to redeem', {
                description: getSafeErrorMessage(error, 'Unable to redeem HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useRedeemRavencoinHTLCManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            htlcId,
            secret,
            initiatorTxId,
            accessCode,
        }: {
            htlcId: number | bigint;
            secret: Uint8Array;
            initiatorTxId: string;
            accessCode: string;
        }) => {
            if (!actor) {
                toast.error('Backend Unavailable', {
                    description: 'Please try again in a moment.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.redeemRavencoinHTLCManual(BigInt(htlcId), secret, initiatorTxId, accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
            toast.success('Swap completed', {
                description: 'The HTLC has been redeemed and swap is complete.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to redeem', {
                description: getSafeErrorMessage(error, 'Unable to redeem HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useRefundRavencoinHTLC() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (htlcId: number | bigint) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before refunding HTLC.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.refundRavencoinHTLC(BigInt(htlcId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            queryClient.invalidateQueries({ queryKey: ['myRavencoinOrders'] });
            toast.success('HTLC refunded', {
                description: 'The HTLC has been refunded and orders are active again.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to refund', {
                description: getSafeErrorMessage(error, 'Unable to refund HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useRefundRavencoinHTLCManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ htlcId, accessCode }: { htlcId: number | bigint; accessCode: string }) => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.refundRavencoinHTLCManual(BigInt(htlcId), accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLCsByWallet'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinOrdersByWallet'] });
            toast.success('HTLC refunded', {
                description: 'The HTLC has been refunded and orders are active again.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to refund', {
                description: getSafeErrorMessage(error, 'Unable to refund HTLC.'),
                duration: 8000,
            });
        },
    });
}

export function useSubmitRavencoinHTLCTxId() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ htlcId, txId, isInitiator }: { htlcId: number | bigint; txId: string; isInitiator: boolean }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Connect your wallet before submitting transaction ID.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.submitRavencoinHTLCTxId(BigInt(htlcId), txId, isInitiator);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myRavencoinHTLCs'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            toast.success('Transaction ID submitted', {
                description: 'Your transaction ID has been recorded.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to submit transaction ID', {
                description: getSafeErrorMessage(error, 'Unable to submit transaction ID.'),
                duration: 8000,
            });
        },
    });
}

export function useSubmitRavencoinHTLCTxIdManual() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            htlcId,
            txId,
            isInitiator,
            accessCode,
        }: {
            htlcId: number | bigint;
            txId: string;
            isInitiator: boolean;
            accessCode: string;
        }) => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.submitRavencoinHTLCTxIdManual(BigInt(htlcId), txId, isInitiator, accessCode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLCsByWallet'] });
            queryClient.invalidateQueries({ queryKey: ['ravencoinHTLC'] });
            toast.success('Transaction ID submitted', {
                description: 'Your transaction ID has been recorded.',
                duration: 5000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to submit transaction ID', {
                description: getSafeErrorMessage(error, 'Unable to submit transaction ID.'),
                duration: 8000,
            });
        },
    });
}

// Temporary Admin Access Hooks

export function useGetTemporaryAccessStatus() {
    const { actor, isFetching } = useActor();

    return useQuery<boolean>({
        queryKey: ['temporaryAccessStatus'],
        queryFn: async () => {
            if (!actor) return true; // Default to enabled if actor not available
            return actor.getTemporaryAccessStatus();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useValidateAdminPasscode() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (passcode: string) => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.validateAdminPasscode(passcode);
        },
        onSuccess: () => {
            // Invalidate both admin status queries to trigger UI update
            queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
            queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Admin Access Granted', {
                description: 'You now have temporary admin access.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            // Don't show error toast to users - admin errors should be silent
            // Only log to console for debugging
            console.error('Admin passcode validation failed:', error);
            // Error is already shown in the modal's error state
        },
    });
}

export function useToggleTemporaryAccess() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (enabled: boolean) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to toggle temporary access.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.toggleTemporaryAccess(enabled);
        },
        onSuccess: (_, enabled) => {
            queryClient.invalidateQueries({ queryKey: ['temporaryAccessStatus'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Temporary Access Updated', {
                description: `Temporary admin access has been ${enabled ? 'enabled' : 'disabled'}.`,
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Update Failed', {
                description: getSafeErrorMessage(error, 'Failed to update temporary access setting.'),
                duration: 8000,
            });
        },
    });
}

export function useUpdateAdminPasscode() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (newPasscode: string) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to update admin passcode.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.updateAdminPasscode(newPasscode);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Passcode Updated', {
                description: 'Admin passcode has been changed successfully.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Passcode Update Failed', {
                description: getSafeErrorMessage(error, 'Failed to update admin passcode.'),
                duration: 8000,
            });
        },
    });
}

export function useClearTemporaryAdminSession() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to clear admin session.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.clearTemporaryAdminSession();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
            queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.info('Admin Session Ended', {
                description: 'Your temporary admin session has been terminated.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Session End Failed', {
                description: getSafeErrorMessage(error, 'Failed to end admin session.'),
                duration: 8000,
            });
        },
    });
}

// Wallet Requirement Hooks

export function useGetWalletRequirementStatus() {
    const { actor, isFetching } = useActor();

    return useQuery<boolean>({
        queryKey: ['walletRequirementStatus'],
        queryFn: async () => {
            if (!actor) return true; // Default to required if actor not available
            return actor.getWalletRequirementStatus();
        },
        enabled: !!actor && !isFetching,
    });
}

export function useToggleWalletRequirement() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (enabled: boolean) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please authenticate with Internet Identity to toggle wallet requirement.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.toggleWalletRequirement(enabled);
        },
        onSuccess: (_, enabled) => {
            queryClient.invalidateQueries({ queryKey: ['walletRequirementStatus'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            toast.success('Wallet Requirement Updated', {
                description: `Wallet connection is now ${enabled ? 'required' : 'optional'} for admin access.`,
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Update Failed', {
                description: getSafeErrorMessage(error, 'Failed to update wallet requirement.'),
                duration: 8000,
            });
        },
    });
}

// Validation Hooks - Admin protected

export function useRunValidationTest() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (category: ValidationCategory) => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.runValidationTest(category);
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['allValidationReports'] });
            queryClient.invalidateQueries({ queryKey: ['validationReport'] });
            queryClient.invalidateQueries({ queryKey: ['validationHistory'] });
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            console.log('[useRunValidationTest] result:', result);
        },
        onError: (error: any) => {
            console.error('[useRunValidationTest] error:', error);
        },
    });
}

export function useGetValidationReport(category: ValidationCategory | null) {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<ValidationReport | null>({
        queryKey: ['validationReport', category],
        queryFn: async () => {
            if (!actor || !category) return null;
            return actor.getValidationReport(category);
        },
        enabled: !!actor && !isFetching && !!category && adminCheckComplete && hasAdminAccess === true,
        retry: false,
    });
}

export function useGetValidationHistory(category: ValidationCategory | null) {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<ValidationResult[]>({
        queryKey: ['validationHistory', category],
        queryFn: async () => {
            if (!actor || !category) return [];
            return actor.getValidationHistory(category);
        },
        enabled: !!actor && !isFetching && !!category && adminCheckComplete && hasAdminAccess === true,
        retry: false,
    });
}

export function useGetAllValidationReports() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<Array<[bigint, ValidationReport]>>({
        queryKey: ['allValidationReports'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAllValidationReports();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: false, // DISABLED - was causing spam
        retry: false,
    });
}

export function useGetAllValidationHistory() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<Array<[bigint, ValidationResult[]]>>({
        queryKey: ['allValidationHistory'],
        queryFn: async () => {
            if (!actor) return [];
            return actor.getAllValidationHistory();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        retry: false,
    });
}

// IC Registry Audit Hook - Admin protected

export function usePerformRegistryAudit() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                // REMOVED: Misleading Internet Identity prompt - actor should be available with AnonymousIdentity
                // Just throw error silently, the onError handler will show appropriate message
                throw new Error('Backend actor not available');
            }
            return actor.performRegistryAudit();
        },
        onSuccess: (_report) => {
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            console.log('[usePerformRegistryAudit] audit complete');
        },
        onError: (error: any) => {
            console.error('[usePerformRegistryAudit] audit failed:', error);
        },
    });
}

// Live Network Re-Verification Hook - Admin protected

export function usePerformLiveNetworkReVerification() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                // REMOVED: Misleading Internet Identity prompt
                throw new Error('Backend actor not available');
            }
            return actor.performLiveNetworkReVerification();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            console.log('[usePerformLiveNetworkReVerification] complete');
        },
        onError: (error: any) => {
            console.error('[usePerformLiveNetworkReVerification] failed:', error);
        },
    });
}

// IC API Verification Hook - Admin protected

export function usePerformICAPIVerification() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                // REMOVED: Misleading Internet Identity prompt
                throw new Error('Backend actor not available');
            }
            return actor.performICAPIVerification();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminActivityLog'] });
            console.log('[usePerformICAPIVerification] complete');
        },
        onError: (error: any) => {
            console.error('[usePerformICAPIVerification] failed:', error);
        },
    });
}

// API Key Management Hooks
export function useGetAllApiKeys() {
    const { actor, isFetching } = useActor();

    return useQuery<[string, ApiKey][]>({
        queryKey: ['apiKeys'],
        queryFn: async () => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.getAllApiKeys();
        },
        enabled: !!actor && !isFetching,
        refetchInterval: 60000, // Reduced from 30s to 60s
    });
}

export function useCreateApiKey() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name, rateLimitPerMinute }: { name: string; rateLimitPerMinute: number }) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.createApiKey(name, BigInt(rateLimitPerMinute));
        },
        onSuccess: (apiKey) => {
            queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
            toast.success('API Key Created', {
                description: `Key: ${apiKey}. Copy it now - you won't be able to see it again!`,
                duration: 10000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Create API Key', {
                description: getSafeErrorMessage(error, 'Could not create API key.'),
                duration: 8000,
            });
        },
    });
}

export function useRevokeApiKey() {
    const { actor } = useActor();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (apiKey: string) => {
            if (!actor) {
                toast.error('Authentication Required', {
                    description: 'Please connect your wallet or authenticate with Internet Identity.',
                    duration: 5000,
                });
                throw new Error('Actor not available');
            }
            return actor.revokeApiKey(apiKey);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
            toast.success('API Key Revoked', {
                description: 'The API key has been revoked and can no longer be used.',
                duration: 6000,
            });
        },
        onError: (error: any) => {
            toast.error('Failed to Revoke API Key', {
                description: getSafeErrorMessage(error, 'Could not revoke API key.'),
                duration: 8000,
            });
        },
    });
}

export function useGetVisitorStats() {
    const { actor, isFetching } = useActor();
    const { data: isAdmin, isFetched: isAdminFetched } = useIsCallerAdmin();
    const { data: isTemporaryAdmin, isFetched: isTempAdminFetched } = useIsCallerTemporaryAdmin();

    const adminCheckComplete = isAdminFetched && isTempAdminFetched;
    const hasAdminAccess = isAdmin || isTemporaryAdmin;

    return useQuery<VisitorStats>({
        queryKey: ['visitorStats'],
        queryFn: async () => {
            if (!actor) {
                throw new Error('Actor not available');
            }
            return actor.getVisitorStats();
        },
        enabled: !!actor && !isFetching && adminCheckComplete && hasAdminAccess === true,
        refetchInterval: 30000, // Refresh every 30 seconds
        retry: false,
    });
}

export function useTrackVisitor() {
    const { actor } = useActor();

    return useMutation({
        mutationFn: async () => {
            if (!actor) {
                // Silently fail if no actor (user not authenticated)
                return;
            }
            try {
                await actor.trackVisitor();
            } catch (error) {
                // Silently fail - visitor tracking shouldn't break the app
                console.warn('[Visitor Tracking] Failed to track visitor:', error);
            }
        },
        retry: false,
    });
}
